export const motionTiming = {
  easeStandard: [0.22, 1, 0.36, 1] as const,
  durationSm: 0.35,
  durationMd: 0.45,
  staggerStep: 0.04
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(REDUCED_MOTION_QUERY).matches
  )
}

function resolveInitial<T extends Record<string, number | string>>(initial: T, animate: T) {
  return prefersReducedMotion() ? animate : initial
}

function resolveDuration(duration: number) {
  return prefersReducedMotion() ? 0 : duration
}

function resolveDelay(delay: number) {
  return prefersReducedMotion() ? 0 : delay
}

const fadeUpInitial = { opacity: 0, y: 16 }
const fadeUpAnimate = { opacity: 1, y: 0 }
export const fadeUpPreset = {
  get initial() {
    return resolveInitial(fadeUpInitial, fadeUpAnimate)
  },
  animate: fadeUpAnimate
}

const fadeInInitial = { opacity: 0 }
const fadeInAnimate = { opacity: 1 }
export const fadeInPreset = {
  get initial() {
    return resolveInitial(fadeInInitial, fadeInAnimate)
  },
  animate: fadeInAnimate
}

const routeShellInitial = { opacity: 0 }
const routeShellAnimate = { opacity: 1 }
export const routeShellPreset = {
  get initial() {
    return resolveInitial(routeShellInitial, routeShellAnimate)
  },
  animate: routeShellAnimate
}

const routePageInitial = { opacity: 0, y: 12, scale: 0.99 }
const routePageAnimate = { opacity: 1, y: 0, scale: 1 }
export const routePagePreset = {
  get initial() {
    return resolveInitial(routePageInitial, routePageAnimate)
  },
  animate: routePageAnimate
}

const sidebarRevealInitial = { opacity: 0, x: -10 }
const sidebarRevealAnimate = { opacity: 1, x: 0 }
export const sidebarRevealPreset = {
  get initial() {
    return resolveInitial(sidebarRevealInitial, sidebarRevealAnimate)
  },
  animate: sidebarRevealAnimate
}

const sidebarLabelInitial = { opacity: 0, x: 8 }
const sidebarLabelAnimate = { opacity: 1, x: 0 }
export const sidebarLabelPreset = {
  get initial() {
    return resolveInitial(sidebarLabelInitial, sidebarLabelAnimate)
  },
  animate: sidebarLabelAnimate
}

const scaleInInitial = { opacity: 0, scale: 0.96 }
const scaleInAnimate = { opacity: 1, scale: 1 }
export const scaleInPreset = {
  get initial() {
    return resolveInitial(scaleInInitial, scaleInAnimate)
  },
  animate: scaleInAnimate
}

const modalBackdropInitial = { opacity: 0 }
const modalBackdropAnimate = { opacity: 1 }
export const modalBackdropPreset = {
  get initial() {
    return resolveInitial(modalBackdropInitial, modalBackdropAnimate)
  },
  animate: modalBackdropAnimate
}

const modalContentInitial = { opacity: 0, y: 16, scale: 0.98 }
const modalContentAnimate = { opacity: 1, y: 0, scale: 1 }
export const modalContentPreset = {
  get initial() {
    return resolveInitial(modalContentInitial, modalContentAnimate)
  },
  animate: modalContentAnimate
}

export function buildStaggerTransition(index: number, baseDelay = 0) {
  return {
    duration: resolveDuration(motionTiming.durationMd),
    ease: motionTiming.easeStandard,
    delay: resolveDelay(baseDelay + index * motionTiming.staggerStep)
  }
}

export function buildFadeTransition(duration = motionTiming.durationSm, delay = 0) {
  return {
    duration: resolveDuration(duration),
    ease: motionTiming.easeStandard,
    delay: resolveDelay(delay)
  }
}

export function buildSidebarItemTransition(index: number) {
  return {
    duration: resolveDuration(motionTiming.durationSm),
    ease: motionTiming.easeStandard,
    delay: resolveDelay(index * 0.04)
  }
}

export function buildSidebarRevealPreset(isRtl: boolean) {
  const xOffset = prefersReducedMotion() ? 0 : isRtl ? 10 : -10
  const initialOpacity = prefersReducedMotion() ? 1 : 0

  return {
    initial: { opacity: initialOpacity, x: xOffset },
    animate: { opacity: 1, x: 0 }
  }
}

export function buildSidebarLabelPreset(isRtl: boolean) {
  const xOffset = prefersReducedMotion() ? 0 : isRtl ? -8 : 8
  const initialOpacity = prefersReducedMotion() ? 1 : 0

  return {
    initial: { opacity: initialOpacity, x: xOffset },
    animate: { opacity: 1, x: 0 }
  }
}

export function buildSlideXPreset(
  direction: 'start' | 'end' = 'start',
  distance = 16,
  isRtl = false
) {
  const sign = direction === 'start' ? -1 : 1
  const xBase = sign * distance
  const xOffset = prefersReducedMotion() ? 0 : isRtl ? -xBase : xBase
  const initialOpacity = prefersReducedMotion() ? 1 : 0

  return {
    initial: { opacity: initialOpacity, x: xOffset },
    animate: { opacity: 1, x: 0 }
  }
}

export function buildHoverLiftInteraction(lift = 4, hoverScale = 1.01) {
  if (prefersReducedMotion()) {
    return {
      whileHover: { y: 0, scale: 1 },
      whilePress: { scale: 1 },
      transition: buildFadeTransition(0)
    }
  }

  return {
    whileHover: { y: -Math.abs(lift), scale: hoverScale },
    whilePress: { scale: Math.max(hoverScale - 0.02, 0.98) },
    transition: buildFadeTransition(motionTiming.durationSm)
  }
}

export function buildPressInteraction(pressScale = 0.98) {
  if (prefersReducedMotion()) {
    return {
      whilePress: { scale: 1 },
      transition: buildFadeTransition(0)
    }
  }

  return {
    whilePress: { scale: pressScale },
    transition: buildFadeTransition(0.15)
  }
}

export function buildModalStaggerTransition(delayChildren = 0.04, staggerChildren = 0.06) {
  return {
    delayChildren: resolveDelay(delayChildren),
    staggerChildren: prefersReducedMotion() ? 0 : staggerChildren
  }
}
